import { useState } from "react";
import { BUSINESS_TYPES } from "../data/businesses";
import { GOAL_OPTIONS } from "../data/prompts";
import { Sparkles, MapPin, ArrowRight, ArrowLeft, ChevronDown, Target, Check, User, Flame } from "lucide-react";
import { trackEvent } from "../lib/analytics";

// Banner de la semana — actualízalo cuando quieras editando este objeto.
// title: aparece grande (incluye emoji si quieres)
// subtext: bajo el título, máx 2 líneas
const PROMPT_DEL_DIA = {
  title: "🔥 Estrategia de la semana",
  subtext:
    "Esta quincena: activa a tus clientes con una promoción de recompra. Genera tus prompts y aplícala hoy.",
};

// Curated list of Mexican cities + "Otra ciudad" (unlocks a free-text field).
const CITIES = [
  "Acapulco",
  "Aguascalientes",
  "Cancún",
  "Chilpancingo",
  "Ciudad de México",
  "Culiacán",
  "Guadalajara",
  "Hermosillo",
  "León",
  "Mérida",
  "Mexicali",
  "Monterrey",
  "Morelia",
  "Oaxaca",
  "Puebla",
  "Querétaro",
  "Saltillo",
  "San Luis Potosí",
  "Tijuana",
  "Veracruz",
];
const OTHER_CITY = "Otra ciudad";

function deriveInitialCityState(initial) {
  const v = (initial || "").trim();
  if (!v) return { sel: "", custom: "" };
  if (CITIES.includes(v)) return { sel: v, custom: "" };
  return { sel: OTHER_CITY, custom: v };
}

export default function Screen1Selector({
  initialBusinessType,
  initialOtherText,
  initialOwnerName,
  initialCity,
  initialGoal,
  onStart,
  onStartDiagnostic,
  onBack,
}) {
  const [selected, setSelected] = useState(initialBusinessType || null);
  const [otherText, setOtherText] = useState(initialOtherText || "");
  const [ownerName, setOwnerName] = useState(initialOwnerName || "");
  const [goal, setGoal] = useState(initialGoal || "");
  const initialCityState = deriveInitialCityState(initialCity);
  const [citySel, setCitySel] = useState(initialCityState.sel);
  const [cityCustom, setCityCustom] = useState(initialCityState.custom);

  const resolvedCity =
    citySel === OTHER_CITY ? cityCustom.trim() : citySel;

  const canContinue =
    selected &&
    goal &&
    resolvedCity.length >= 2 &&
    (selected !== "otro" || otherText.trim().length >= 3);

  const handleContinue = () => {
    if (!canContinue) return;
    onStart({
      businessType: selected,
      otherText: otherText.trim(),
      ownerName: ownerName.trim(),
      city: resolvedCity,
      goal,
    });
  };

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="screen-1"
    >
      {/* Brand */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
            <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nego<span className="text-orange-500">IA</span>
          </span>
        </div>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            data-testid="screen-1-back-button"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 active:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
            Volver
          </button>
        )}
      </div>
      <p
        className="text-xs font-medium text-slate-500 mb-8 ml-11 -mt-1"
        data-testid="brand-tagline"
      >
        Tu asistente de IA para vender más
      </p>

      {/* Headline */}
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
        ¿Qué tipo de negocio <span className="text-orange-500">tienes</span>?
      </h1>
      <p className="text-base text-slate-500 mt-3 leading-relaxed">
        Genera estrategias de venta con IA en 2 minutos —{" "}
        <span className="font-semibold text-teal-700">gratis</span>.
      </p>

      {/* Diagnostic CTA — launches the standalone 12-question business diagnostic */}
      {onStartDiagnostic && (
        <button
          type="button"
          onClick={onStartDiagnostic}
          data-testid="open-diagnostic-button"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold text-base py-3.5 rounded-2xl shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
        >
          🔍 Diagnostica tu negocio
        </button>
      )}

      {/* Social proof counter */}
      <p
        className="text-center font-semibold mt-4"
        style={{ color: "#F97316", fontSize: "12px" }}
        data-testid="social-proof-counter"
      >
        🔥 +300 estrategias generadas para negocios mexicanos
      </p>

      {/* Real testimonials */}
      <blockquote
        className="mt-3 rounded-r-lg py-3 pl-3 pr-4 border-l-4"
        style={{ backgroundColor: "#EAF3DE", borderLeftColor: "#16a34a" }}
        data-testid="testimonial-jose"
      >
        <p className="text-xs text-slate-600 leading-snug">
          ⭐ &ldquo;Puse las promociones y generé 2 o 3 ventas que no hubiera hecho sin la app&rdquo;
        </p>
        <footer className="text-[11px] text-slate-500 mt-1 leading-tight">
          — José, puesto de comida · Acapulco
        </footer>
      </blockquote>

      <blockquote
        className="mt-2 rounded-r-lg py-3 pl-3 pr-4 border-l-4"
        style={{ backgroundColor: "#EAF3DE", borderLeftColor: "#16a34a" }}
        data-testid="testimonial-jair"
      >
        <p className="text-xs text-slate-600 leading-snug">
          ⭐ &ldquo;Está muy fácil y útil, sin duda la recomendaría&rdquo;
        </p>
        <footer className="text-[11px] text-slate-500 mt-1 leading-tight">
          — Jair, gimnasio funcional · Acapulco
        </footer>
      </blockquote>

      {/* Weekly banner — content in PROMPT_DEL_DIA constant above */}
      <div
        className="mt-6 rounded-xl border-2 p-4 flex items-start gap-3"
        style={{ backgroundColor: "#FFF7ED", borderColor: "#F97316" }}
        data-testid="weekly-banner"
      >
        <span
          className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0"
          style={{ backgroundColor: "#F97316" }}
        >
          <Flame className="w-5 h-5 text-white" strokeWidth={2.4} />
        </span>
        <div className="min-w-0">
          <p
            className="text-sm font-bold leading-snug"
            style={{ color: "#9A3412" }}
            data-testid="weekly-banner-title"
          >
            {PROMPT_DEL_DIA.title}
          </p>
          <p
            className="text-xs text-slate-700 mt-1 leading-snug"
            data-testid="weekly-banner-subtext"
          >
            {PROMPT_DEL_DIA.subtext}
          </p>
        </div>
      </div>

      {/* Owner/business name (optional) — shown before the business grid */}
      <div className="mt-8">
        <label
          htmlFor="owner-name"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-2"
        >
          <User className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿Cómo se llama tu negocio?{" "}
          <span className="text-slate-400 font-medium">(opcional)</span>
        </label>
        <input
          id="owner-name"
          type="text"
          inputMode="text"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="Ej: Pollos Yuri, Cápsula Evolution..."
          maxLength={60}
          data-testid="owner-name-input"
          className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400"
        />
      </div>

      {/* Business grid */}
      <div className="grid grid-cols-2 gap-3 mt-8">
        {BUSINESS_TYPES.map((b) => {
          const Icon = b.icon;
          const isSelected = selected === b.id;
          return (
            <button
              key={b.id}
              type="button"
              data-testid={`business-type-card-${b.id}`}
              onClick={() => {
                setSelected(b.id);
                trackEvent("selecciono_giro", { giro: b.id });
              }}
              className={[
                "group flex flex-col items-center justify-center aspect-square rounded-2xl border-2 p-4",
                "transition-all duration-200 active:scale-95",
                isSelected
                  ? "border-orange-500 bg-orange-50 shadow-lg shadow-orange-500/20"
                  : "border-slate-200 bg-white hover:border-slate-300",
              ].join(" ")}
            >
              <Icon
                className={[
                  "w-9 h-9 mb-3 transition-colors",
                  isSelected ? "text-orange-500" : "text-teal-700",
                ].join(" ")}
                strokeWidth={1.8}
              />
              <span
                className={[
                  "text-sm font-semibold text-center leading-tight",
                  isSelected ? "text-slate-900" : "text-slate-700",
                ].join(" ")}
              >
                {b.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Otro field */}
      {selected === "otro" && (
        <div className="mt-5 animate-scale-in" data-testid="other-business-wrapper">
          <label className="block text-sm font-semibold text-slate-800 mb-2">
            Cuéntanos rápido qué vendes
          </label>
          <input
            type="text"
            inputMode="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Ej: dulcería, papelería, estética..."
            maxLength={80}
            data-testid="other-business-input"
            className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400"
          />
        </div>
      )}

      {/* Goal question (shown only after a business is selected) */}
      {selected && (
        <div className="mt-8 animate-scale-in" data-testid="goal-wrapper">
          <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-3">
            <Target className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
            ¿Qué quieres lograr hoy?
          </label>
          <div className="flex flex-col gap-2.5">
            {GOAL_OPTIONS.map((opt) => {
              const isSelected = goal === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setGoal(opt)}
                  data-testid={`goal-option-${opt}`}
                  className={[
                    "flex items-center justify-between text-left px-4 py-3 rounded-xl border-2 transition-all duration-150 active:scale-[0.99]",
                    isSelected
                      ? "border-teal-600 bg-teal-50"
                      : "border-slate-200 bg-white active:bg-slate-50",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "text-sm font-semibold leading-snug pr-2",
                      isSelected ? "text-teal-900" : "text-slate-800",
                    ].join(" ")}
                  >
                    {opt}
                  </span>
                  {isSelected && (
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-teal-600 text-white shrink-0">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* City dropdown (native <select> = best iOS Safari UX) */}
      <div className="mt-6">
        <label
          htmlFor="city-select"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 mb-2"
        >
          <MapPin className="w-4 h-4 text-teal-700" strokeWidth={2.2} />
          ¿En qué ciudad estás?
        </label>
        <div className="relative">
          <select
            id="city-select"
            value={citySel}
            onChange={(e) => setCitySel(e.target.value)}
            data-testid="city-select"
            className="appearance-none w-full p-4 pr-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all"
          >
            <option value="" disabled>
              Selecciona tu ciudad
            </option>
            {CITIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value={OTHER_CITY}>{OTHER_CITY}</option>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500"
            strokeWidth={2.2}
          />
        </div>

        {citySel === OTHER_CITY && (
          <input
            type="text"
            inputMode="text"
            value={cityCustom}
            onChange={(e) => setCityCustom(e.target.value)}
            placeholder="Escribe tu ciudad"
            maxLength={60}
            data-testid="city-custom-input"
            className="mt-3 w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-teal-600 focus:bg-white outline-none transition-all placeholder:text-slate-400 animate-scale-in"
          />
        )}
        <p className="text-xs text-slate-400 mt-1.5">
          Usamos tu ciudad para que los prompts suenen más locales.
        </p>
      </div>

      {/* Spacer pushes CTA down */}
      <div className="flex-1" />

      {/* CTA */}
      <div className="mt-10 pb-safe">
        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          data-testid="start-button"
          className={[
            "w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold",
            "transition-all duration-200",
            canContinue
              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed",
          ].join(" ")}
        >
          Comenzar
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </button>
        <p className="text-center text-xs text-slate-400 mt-3">
          Sin registro. Sin tarjeta. 100% gratis.
        </p>
      </div>
    </div>
  );
}
